import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Cart drawer as a modal dialog-drawer (mirrors the Usage Details drawer).
 *
 * Extends the theme's `DialogComponent`, so the panel opens with `showModal()` and gets a
 * native `::backdrop` on every viewport — unlike the stock `<theme-drawer>`, which is
 * non-modal on desktop and never dimmed the page.
 *
 * To avoid touching the header / standard-actions wiring, it keeps a ThemeDrawer-compatible
 * surface: `open()` / `close()` / `toggle()` / `isOpen` and an `open` attribute mirroring the
 * dialog's state. It intentionally does NOT re-emit `theme-drawer:open/close` — those events
 * trigger the theme's page-squeeze (`.page-wrapper--drawer-open`), which we don't want for a
 * modal drawer — so it syncs the trigger's `aria-expanded` directly instead.
 *
 * Cart-specific concerns are preserved from the stock component: auto-open on add-to-cart,
 * sticky-summary sizing, and closing the drawer when the installments CTA is clicked.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    // The sessionStorage restore path can leave the dialog already open before this module
    // loads, so reconcile on connect.
    if (this.refs.dialog?.open) this.#handleOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  /** @returns {boolean} Whether the drawer is currently open. */
  get isOpen() {
    return Boolean(this.refs.dialog?.open);
  }

  /* ---- ThemeDrawer-compatible API (header trigger, Shopify.actions.openCart) ---- */

  open() {
    this.showDialog();
  }

  close() {
    return this.closeDialog();
  }

  toggle() {
    this.toggleDialog();
  }

  /**
   * Syncs the header trigger's aria-expanded to match the drawer's open state.
   * @param {boolean} expanded
   */
  #syncTriggerAria(expanded) {
    document
      .querySelector('[aria-controls="cart-drawer"]')
      ?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Mirrors the open state onto the host as an attribute, keeps the trigger's aria in sync,
   * and runs the cart-specific open work.
   */
  #handleOpen = () => {
    this.setAttribute('open', '');
    this.#syncTriggerAria(true);
    this.#updateStickyState();

    // Close the cart drawer when the installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that replace the
    // shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.close(), { once: true });
    });
  };

  #handleClose = () => {
    this.removeAttribute('open');
    this.#syncTriggerAria(false);
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.isOpen;

    // When the add originates inside another open MODAL <dialog> (e.g. quick-add), defer the
    // auto-open until that dialog's `close` fires so its focus restoration runs first —
    // otherwise the dialog's focus trap fights ours. Non-modal dialogs are excluded via `:modal`.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.isOpen) this.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    // Refs don't cross the nested cart-items-component boundary, so query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Keep the dialog out of "sticky" mode when there's no summary (e.g. empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
